import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';

/**
 * Floating "Back to article" pill that appears in the bottom-right corner
 * while the reader is inside an interactive HTML embed.
 *
 * Visible when: the article title has scrolled out of view AND at least
 * one .html-embed-wrapper is currently visible in the viewport.
 */
export const BackToArticlePill = ({ targetSelector = '[data-testid="article-title"]' }) => {
  const titleInViewRef = useRef(true);
  const embedInViewCountRef = useRef(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const recompute = () => {
      setVisible(!titleInViewRef.current && embedInViewCountRef.current > 0);
    };

    const titleObserver = new IntersectionObserver(
      ([entry]) => {
        titleInViewRef.current = entry.isIntersecting;
        recompute();
      },
      { threshold: 0 }
    );

    const embedObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          embedInViewCountRef.current += e.isIntersecting ? 1 : -1;
          if (embedInViewCountRef.current < 0) embedInViewCountRef.current = 0;
        });
        recompute();
      },
      { threshold: 0 }
    );

    let attached = false;
    const tryAttach = () => {
      const title = document.querySelector(targetSelector);
      const embeds = document.querySelectorAll('.article-body .html-embed-wrapper');
      if (title) titleObserver.observe(title);
      embeds.forEach((el) => embedObserver.observe(el));
      attached = title || embeds.length > 0;
      return Boolean(attached);
    };

    // Retry a few times in case React hasn't finished rendering yet
    let tries = 0;
    const interval = setInterval(() => {
      tries += 1;
      if (tryAttach() || tries > 12) clearInterval(interval);
    }, 250);

    return () => {
      clearInterval(interval);
      titleObserver.disconnect();
      embedObserver.disconnect();
    };
  }, [targetSelector]);

  const handleClick = () => {
    const target = document.querySelector(targetSelector);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="Back to article"
      data-testid="back-to-article-pill"
      className={`
        fixed bottom-6 right-6 z-50
        inline-flex items-center gap-2 px-4 py-2.5
        rounded-full shadow-lg
        bg-foreground text-background
        text-sm font-medium
        transition-all duration-300 ease-out
        hover:scale-105 hover:shadow-xl
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent
        ${visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}
      `}
    >
      <ArrowUp className="w-4 h-4" />
      Back to article
    </button>
  );
};

export default BackToArticlePill;
